/**
 * Browser-based AI Inference using ONNX Runtime Web
 *
 * Runs NER, embeddings, classification, and sentiment analysis
 * on the analyst's local GPU (WebGPU/WASM) via ONNX Runtime.
 * Models are downloaded once and cached in IndexedDB.
 */

import { ModelCache } from './model-cache';

// ONNX Runtime types (loaded dynamically)
type InferenceSession = {
  run(feeds: Record<string, unknown>): Promise<Record<string, { data: Float32Array; dims: number[] }>>;
  release(): Promise<void>;
};

type OrtModule = {
  InferenceSession: {
    create(
      buffer: ArrayBuffer,
      options?: { executionProviders?: string[] }
    ): Promise<InferenceSession>;
  };
  Tensor: new (type: string, data: Float32Array | BigInt64Array | Int32Array, dims: number[]) => unknown;
  env: {
    wasm: { numThreads: number; simd: boolean };
  };
};

export interface ModelInfo {
  id: string;
  name: string;
  description: string;
  url: string;
  size_mb: number;
  type: 'ner' | 'embeddings' | 'classification' | 'sentiment';
}

export interface NEREntity {
  text: string;
  label: string;
  start: number;
  end: number;
  score: number;
}

export interface ClassificationResult {
  label: string;
  score: number;
}

export interface SentimentResult {
  label: 'positive' | 'negative' | 'neutral';
  score: number;
  scores: { positive: number; negative: number; neutral: number };
}

// Default model catalog
const DEFAULT_MODELS: ModelInfo[] = [
  {
    id: 'ner-bert-base',
    name: 'BERT NER',
    description: 'Named Entity Recognition (BERT base)',
    url: '/api/models/ner-bert-base.onnx',
    size_mb: 170,
    type: 'ner',
  },
  {
    id: 'sentence-transformer-mini',
    name: 'MiniLM Embeddings',
    description: 'Sentence embeddings (all-MiniLM-L6-v2)',
    url: '/api/models/sentence-transformer-mini.onnx',
    size_mb: 80,
    type: 'embeddings',
  },
  {
    id: 'doc-classifier',
    name: 'Document Classifier',
    description: 'Document type classification',
    url: '/api/models/doc-classifier.onnx',
    size_mb: 120,
    type: 'classification',
  },
  {
    id: 'sentiment-distilbert',
    name: 'Sentiment Analysis',
    description: 'Sentiment analysis (DistilBERT)',
    url: '/api/models/sentiment-distilbert.onnx',
    size_mb: 100,
    type: 'sentiment',
  },
];

export class BrowserAIEngine {
  private ort: OrtModule | null = null;
  private sessions = new Map<string, InferenceSession>();
  private modelCache: ModelCache;
  private _isGPUAccelerated = false;
  private _initialized = false;

  constructor() {
    this.modelCache = new ModelCache();
  }

  async init(): Promise<void> {
    if (this._initialized) return;

    try {
      // Dynamically import ONNX Runtime Web
      const ortModule = await import('onnxruntime-web');
      this.ort = ortModule as unknown as OrtModule;

      // Configure ONNX Runtime
      if (this.ort.env?.wasm) {
        this.ort.env.wasm.numThreads = Math.min(navigator.hardwareConcurrency || 4, 4);
        this.ort.env.wasm.simd = true;
      }

      // Check WebGPU availability for ONNX
      if ('gpu' in navigator) {
        try {
          const adapter = await (navigator as { gpu: { requestAdapter(): Promise<unknown> } }).gpu.requestAdapter();
          this._isGPUAccelerated = !!adapter;
        } catch {
          this._isGPUAccelerated = false;
        }
      }

      this._initialized = true;
      console.log(
        `[BrowserAI] Initialized (GPU accelerated: ${this._isGPUAccelerated})`
      );
    } catch (err) {
      console.error('[BrowserAI] Failed to load ONNX Runtime:', err);
      throw new Error(
        'ONNX Runtime Web could not be loaded. Install it with: npm install onnxruntime-web'
      );
    }
  }

  async loadModel(
    modelId: string,
    onProgress?: (loaded: number, total: number) => void
  ): Promise<void> {
    if (!this.ort) throw new Error('BrowserAI not initialized');
    if (this.sessions.has(modelId)) return;

    const modelInfo = DEFAULT_MODELS.find((m) => m.id === modelId);
    if (!modelInfo) throw new Error(`Unknown model: ${modelId}`);

    let modelBuffer: ArrayBuffer;

    // Check cache first
    const cached = await this.modelCache.getModel(modelId);
    if (cached) {
      modelBuffer = cached;
      onProgress?.(cached.byteLength, cached.byteLength);
    } else {
      // Download model
      const response = await fetch(modelInfo.url);
      if (!response.ok) {
        throw new Error(`Failed to download model ${modelId}: ${response.status}`);
      }

      const contentLength = parseInt(response.headers.get('content-length') || '0', 10);
      const reader = response.body?.getReader();

      if (!reader) {
        modelBuffer = await response.arrayBuffer();
      } else {
        const chunks: Uint8Array[] = [];
        let loaded = 0;

        while (true) {
          const { done, value } = await reader.read();
          if (done) break;
          chunks.push(value);
          loaded += value.byteLength;
          onProgress?.(loaded, contentLength || modelInfo.size_mb * 1024 * 1024);
        }

        // Concatenate chunks
        const total = chunks.reduce((sum, c) => sum + c.byteLength, 0);
        modelBuffer = new ArrayBuffer(total);
        const view = new Uint8Array(modelBuffer);
        let offset = 0;
        for (const chunk of chunks) {
          view.set(chunk, offset);
          offset += chunk.byteLength;
        }
      }

      // Cache the model
      await this.modelCache.saveModel(modelId, modelBuffer);
    }

    // Create inference session
    const providers: string[] = [];
    if (this._isGPUAccelerated) providers.push('webgpu');
    providers.push('wasm');

    const session = await this.ort.InferenceSession.create(modelBuffer, {
      executionProviders: providers,
    });

    this.sessions.set(modelId, session);
    console.log(`[BrowserAI] Model loaded: ${modelId} (${(modelBuffer.byteLength / 1024 / 1024).toFixed(1)} MB)`);
  }

  async runNER(text: string): Promise<NEREntity[]> {
    if (!this.ort) throw new Error('BrowserAI not initialized');

    await this.loadModel('ner-bert-base');
    const session = this.sessions.get('ner-bert-base');
    if (!session) throw new Error('NER model not loaded');

    // Simple tokenization (production would use a proper tokenizer)
    const tokens = text.split(/\s+/);
    const inputIds = new Int32Array(tokens.length);
    const attentionMask = new Int32Array(tokens.length).fill(1);

    // Simple hash-based token IDs (placeholder for real tokenizer)
    for (let i = 0; i < tokens.length; i++) {
      let hash = 0;
      for (let j = 0; j < tokens[i].length; j++) {
        hash = (hash * 31 + tokens[i].charCodeAt(j)) & 0x7fffffff;
      }
      inputIds[i] = hash % 30000;
    }

    try {
      const feeds: Record<string, unknown> = {
        input_ids: new this.ort.Tensor('int32', inputIds, [1, tokens.length]),
        attention_mask: new this.ort.Tensor('int32', attentionMask, [1, tokens.length]),
      };

      const output = await session.run(feeds);
      const logits = output.logits || output.output || Object.values(output)[0];

      if (!logits) return [];

      // Parse NER output
      const entities: NEREntity[] = [];
      const labelMap = ['O', 'B-PER', 'I-PER', 'B-ORG', 'I-ORG', 'B-LOC', 'I-LOC', 'B-MISC', 'I-MISC'];
      const numLabels = labelMap.length;

      let currentEntity: NEREntity | null = null;
      let charOffset = 0;

      for (let i = 0; i < tokens.length; i++) {
        // Get predicted label (argmax)
        let maxIdx = 0;
        let maxVal = -Infinity;
        for (let l = 0; l < numLabels; l++) {
          const val = logits.data[i * numLabels + l] ?? 0;
          if (val > maxVal) {
            maxVal = val;
            maxIdx = l;
          }
        }

        const label = labelMap[maxIdx] || 'O';
        const score = Math.exp(maxVal) / (1 + Math.exp(maxVal)); // sigmoid approximation

        if (label.startsWith('B-')) {
          if (currentEntity) entities.push(currentEntity);
          currentEntity = {
            text: tokens[i],
            label: label.slice(2),
            start: charOffset,
            end: charOffset + tokens[i].length,
            score,
          };
        } else if (label.startsWith('I-') && currentEntity) {
          currentEntity.text += ' ' + tokens[i];
          currentEntity.end = charOffset + tokens[i].length;
          currentEntity.score = Math.min(currentEntity.score, score);
        } else {
          if (currentEntity) {
            entities.push(currentEntity);
            currentEntity = null;
          }
        }

        charOffset += tokens[i].length + 1;
      }

      if (currentEntity) entities.push(currentEntity);
      return entities;
    } catch (err) {
      console.error('[BrowserAI] NER inference failed:', err);
      return [];
    }
  }

  async computeEmbeddings(texts: string[]): Promise<Float32Array[]> {
    if (!this.ort) throw new Error('BrowserAI not initialized');

    await this.loadModel('sentence-transformer-mini');
    const session = this.sessions.get('sentence-transformer-mini');
    if (!session) throw new Error('Embedding model not loaded');

    const embeddings: Float32Array[] = [];

    for (const text of texts) {
      const tokens = text.split(/\s+/).slice(0, 128);
      const inputIds = new Int32Array(tokens.length);
      const attentionMask = new Int32Array(tokens.length).fill(1);

      for (let i = 0; i < tokens.length; i++) {
        let hash = 0;
        for (let j = 0; j < tokens[i].length; j++) {
          hash = (hash * 31 + tokens[i].charCodeAt(j)) & 0x7fffffff;
        }
        inputIds[i] = hash % 30000;
      }

      try {
        const feeds: Record<string, unknown> = {
          input_ids: new this.ort.Tensor('int32', inputIds, [1, tokens.length]),
          attention_mask: new this.ort.Tensor('int32', attentionMask, [1, tokens.length]),
        };

        const output = await session.run(feeds);
        const embedding = output.sentence_embedding || output.output || Object.values(output)[0];

        if (embedding) {
          embeddings.push(new Float32Array(embedding.data));
        }
      } catch (err) {
        console.error('[BrowserAI] Embedding inference failed for text:', err);
        // Return zero vector as fallback
        embeddings.push(new Float32Array(384));
      }
    }

    return embeddings;
  }

  async classifyDocument(text: string): Promise<ClassificationResult[]> {
    if (!this.ort) throw new Error('BrowserAI not initialized');

    await this.loadModel('doc-classifier');
    const session = this.sessions.get('doc-classifier');
    if (!session) throw new Error('Classification model not loaded');

    const tokens = text.split(/\s+/).slice(0, 512);
    const inputIds = new Int32Array(tokens.length);
    const attentionMask = new Int32Array(tokens.length).fill(1);

    for (let i = 0; i < tokens.length; i++) {
      let hash = 0;
      for (let j = 0; j < tokens[i].length; j++) {
        hash = (hash * 31 + tokens[i].charCodeAt(j)) & 0x7fffffff;
      }
      inputIds[i] = hash % 30000;
    }

    try {
      const feeds: Record<string, unknown> = {
        input_ids: new this.ort.Tensor('int32', inputIds, [1, tokens.length]),
        attention_mask: new this.ort.Tensor('int32', attentionMask, [1, tokens.length]),
      };

      const output = await session.run(feeds);
      const logits = output.logits || output.output || Object.values(output)[0];

      if (!logits) return [];

      const classLabels = [
        'Financial Report', 'Legal Document', 'Intelligence Brief',
        'Communication', 'Technical Report', 'News Article', 'Other',
      ];

      // Softmax
      const scores = Array.from(logits.data).slice(0, classLabels.length);
      const maxScore = Math.max(...scores);
      const expScores = scores.map((s) => Math.exp(s - maxScore));
      const sumExp = expScores.reduce((a, b) => a + b, 0);
      const probs = expScores.map((s) => s / sumExp);

      return classLabels
        .map((label, i) => ({ label, score: probs[i] }))
        .sort((a, b) => b.score - a.score);
    } catch (err) {
      console.error('[BrowserAI] Classification failed:', err);
      return [];
    }
  }

  async analyzeSentiment(text: string): Promise<SentimentResult> {
    if (!this.ort) throw new Error('BrowserAI not initialized');

    await this.loadModel('sentiment-distilbert');
    const session = this.sessions.get('sentiment-distilbert');
    if (!session) {
      return {
        label: 'neutral',
        score: 0.5,
        scores: { positive: 0.33, negative: 0.33, neutral: 0.34 },
      };
    }

    const tokens = text.split(/\s+/).slice(0, 256);
    const inputIds = new Int32Array(tokens.length);
    const attentionMask = new Int32Array(tokens.length).fill(1);

    for (let i = 0; i < tokens.length; i++) {
      let hash = 0;
      for (let j = 0; j < tokens[i].length; j++) {
        hash = (hash * 31 + tokens[i].charCodeAt(j)) & 0x7fffffff;
      }
      inputIds[i] = hash % 30000;
    }

    try {
      const feeds: Record<string, unknown> = {
        input_ids: new this.ort.Tensor('int32', inputIds, [1, tokens.length]),
        attention_mask: new this.ort.Tensor('int32', attentionMask, [1, tokens.length]),
      };

      const output = await session.run(feeds);
      const logits = output.logits || output.output || Object.values(output)[0];

      if (!logits) {
        return {
          label: 'neutral',
          score: 0.5,
          scores: { positive: 0.33, negative: 0.33, neutral: 0.34 },
        };
      }

      const raw = Array.from(logits.data).slice(0, 3);
      const maxScore = Math.max(...raw);
      const expScores = raw.map((s) => Math.exp(s - maxScore));
      const sumExp = expScores.reduce((a, b) => a + b, 0);
      const probs = expScores.map((s) => s / sumExp);

      const labels: Array<'negative' | 'neutral' | 'positive'> = ['negative', 'neutral', 'positive'];
      const maxIdx = probs.indexOf(Math.max(...probs));

      return {
        label: labels[maxIdx],
        score: probs[maxIdx],
        scores: {
          negative: probs[0],
          neutral: probs[1],
          positive: probs[2],
        },
      };
    } catch (err) {
      console.error('[BrowserAI] Sentiment analysis failed:', err);
      return {
        label: 'neutral',
        score: 0.5,
        scores: { positive: 0.33, negative: 0.33, neutral: 0.34 },
      };
    }
  }

  getAvailableModels(): ModelInfo[] {
    return DEFAULT_MODELS.map((m) => ({
      ...m,
    }));
  }

  getLoadedModels(): string[] {
    return Array.from(this.sessions.keys());
  }

  isModelLoaded(modelId: string): boolean {
    return this.sessions.has(modelId);
  }

  isGPUAccelerated(): boolean {
    return this._isGPUAccelerated;
  }

  async dispose(): Promise<void> {
    for (const [id, session] of this.sessions) {
      try {
        await session.release();
      } catch (err) {
        console.warn(`[BrowserAI] Failed to release session ${id}:`, err);
      }
    }
    this.sessions.clear();
    this._initialized = false;
    console.log('[BrowserAI] Engine disposed');
  }
}
